import React, { useCallback, useRef, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Button, Grid, Modal } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  addNewProduct,
  addUploads,
  getSelectedGroupProducts,
  getSelectedProduct,
  getSelectedReason,
  getSelectedSubReason,
  getUploadMediaIds,
  mobSelected,
  returnQuantity,
  returnType,
} from "features/product/productSlice";
import { ReactComponent as CloseIcon } from "assets/close.svg";
import { useEffect } from "react";
import {
  getAttributeProducts,
  getAttributes,
  getDifferentVariants,
  getSameVariants,
} from "utils/api";
import { getLineItemMetadata } from "utils/lineItemMetadata";
import { getMerchantConfig, getRmsConfig } from "features/config/configSlice";
import { ReactComponent as Search } from "assets/search.svg";
import { StyledDifferentProduct } from "./styled";
import {
  addReturnedProducts,
  getReturnedProducts,
} from "features/returnedProducts/returnedProductSlice";
import {
  getSelectedOrder,
  updateWebSelected,
} from "features/orders/orderSlice";
import { toast } from "react-toastify";
import "react-lazy-load-image-component/src/effects/opacity.css";
import SizeSelector from "./SizeSelector";
import parse from "html-react-parser";
import LoadingError from "components/v2/Common/LoadingError";
import FloatingCloseButton from "components/v2/Common/FloatingCloseButton";
import { getEffectiveResolutionConfig } from "utils/reasonResolution";

function DifferentProduct({
  quantityInc,
  productType,
  requiredProduct: externalRequiredProduct,
  setRequiredProduct: setExternalRequiredProduct,
  selectedVariant,
  setSelectedVariant,
  otherReason,
  setAnimate,
  setUploads,
  onExchangeComplete,
  actionLabel = "Select for exchange",
  pageMode = false,
}) {
  const [query, setQuery] = useState(null);
  const [offset, setOffset] = useState(0);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [differentProductLoader, setDifferentProductLoader] = useState(true);
  const [differentProducts, setDifferentProducts] = useState([]);
  const [collections, setCollections] = useState([]);
  const [collectionLoader, setCollectionLoader] = useState(false);
  const [collectionId, setCollectionId] = useState(null);
  const [viewMoreLoader, setViewMoreLoader] = useState(false);
  const [differentApiError, setDifferentApiError] = useState(false);
  const [variantApiError, setVariantApiError] = useState(false);
  const [showViewMore, setShowViewMore] = useState(true);
  const [searchInput, setSearchInput] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState({});
  const [variantsLoader, setVariantsLoader] = useState(false);
  const [localSelectedVariant, setLocalSelectedVariant] = useState(null);
  const [localRequiredProduct, setLocalRequiredProduct] = useState(null);
  const [customProperties, setCustomProperties] = useState([]);
  const [customSizeError, setCustomSizeError] = useState(false);
  const [customProductGroupId, setCustomProductGroupId] = useState("");
  const [openFilter, setOpenFilter] = useState(false);
  const [activeFilter, setActiveFilter] = useState(false);
  const [allAttributes, setAllAttributes] = useState([]);
  const [openAttributeValues, setOpenAttributeValues] = useState([]);
  const [openAttributeId, setOpenAttributeId] = useState("");
  const [attributeData, setAttributeData] = useState([]);
  const [filteredVariantProducts, setFilteredVariantProducts] = useState([]);
  const [selectedFilterVariantId, setSelectedFilterVariantId] = useState(null);
  const [discountPrice, setDiscountPrice] = useState(0);
  const [activeListProductId, setActiveListProductId] = useState(null);
  const [isSelectionDrawerOpen, setIsSelectionDrawerOpen] = useState(false);
  const infiniteScrollRef = useRef(null);
  const replacementSearchRef = useRef(null);
  const activeListProductIdRef = useRef(null);
  const isSelectionDrawerOpenRef = useRef(false);
  const variantsLoaderRef = useRef(false);
  const selectedProductDetailRef = useRef(null);
  const productsRequestInFlightRef = useRef(false);
  const productsRequestKeyRef = useRef("");
  const productsRequestSeqRef = useRef(0);
  const variantRequestInFlightRef = useRef(false);
  const variantRequestKeyRef = useRef("");
  const drawerOpenedAtRef = useRef(0);
  const [isReplacementSearchStuck, setIsReplacementSearchStuck] =
    useState(false);
  const [sameVariantConfirmationOpen, setSameVariantConfirmationOpen] =
    useState(false);

  const mapVariantAttributes = (variant) => {
    const mapping = {};
    if (variant && Array.isArray(variant.attributes)) {
      variant.attributes.forEach((attr) => {
        mapping[attr.product_attribute] = attr.value;
      });
    }
    return mapping;
  };

  const selectedOrder = useSelector(getSelectedOrder);
  const selectedProduct = useSelector(getSelectedProduct);
  const merchantConfig = useSelector(getMerchantConfig);
  const rmsConfig = useSelector(getRmsConfig);
  const returnedProducts = useSelector(getReturnedProducts);
  const reason = useSelector(getSelectedReason);
  const subReason = useSelector(getSelectedSubReason);
  const selectedGroupProducts = useSelector(getSelectedGroupProducts);
  const uploadMediaIds = useSelector(getUploadMediaIds);

  const dispatch = useDispatch();
  const requiredProduct = pageMode
    ? localRequiredProduct
    : externalRequiredProduct;
  const effectiveSelectedVariant =
    localSelectedVariant !== null ? localSelectedVariant : selectedVariant;

  const isOriginalVariant = useCallback(
    (variant) =>
      variant &&
      selectedProduct &&
      String(variant.variant_id) === String(selectedProduct.variant_id),
    [selectedProduct],
  );

  const isSameVariantSelected = isOriginalVariant(effectiveSelectedVariant);

  const openSameVariantConfirmationIfNeeded = useCallback((variant) => {
    if (isOriginalVariant(variant)) {
      setSameVariantConfirmationOpen(true);
    }
  }, [isOriginalVariant]);

  useEffect(() => {
    const updateSearchStickiness = () => {
      if (!replacementSearchRef.current) {
        return;
      }
      const rect = replacementSearchRef.current.getBoundingClientRect();
      setIsReplacementSearchStuck(rect.top <= 72 && window.scrollY > 0);
    };

    updateSearchStickiness();
    window.addEventListener("scroll", updateSearchStickiness, {
      passive: true,
    });
    window.addEventListener("resize", updateSearchStickiness);

    return () => {
      window.removeEventListener("scroll", updateSearchStickiness);
      window.removeEventListener("resize", updateSearchStickiness);
    };
  }, []);

  const updateRequiredProduct = (product) => {
    if (pageMode) {
      setLocalRequiredProduct(product);
      return;
    }
    setExternalRequiredProduct(product);
  };

  const pickInitialVariant = (variants = []) => {
    if (!Array.isArray(variants) || variants.length === 0) {
      return null;
    }
    return (
      variants.find((v) => v?.variant_id !== selectedProduct.variant_id) ||
      variants[0]
    );
  };

  const buildDrawerProduct = (product) => {
    const variants = Array.isArray(product?.variants)
      ? [...product.variants].sort(
          (a, b) => (a?.position || 0) - (b?.position || 0),
        )
      : [];

    return {
      ...product,
      attributes: Array.isArray(product?.attributes) ? product.attributes : [],
      variants,
    };
  };

  const clearFilterState = () => {
    setAttributeData([]);
    setFilteredVariantProducts([]);
    setActiveFilter(false);
    setOpenFilter(false);
  };

  const isValueSelected = (valueId) =>
    attributeData.some((attr) => attr.values.includes(valueId));

  const toggleAttributeValue = (attributeId, valueId) => {
    setAttributeData((prev) => {
      const existingAttr = prev.find((item) => item.id === attributeId);

      if (!existingAttr) {
        return [...prev, { id: attributeId, values: [valueId] }];
      }

      const valueExists = existingAttr.values.includes(valueId);
      const updatedValues = valueExists
        ? existingAttr.values.filter((value) => value !== valueId)
        : [...existingAttr.values, valueId];

      if (updatedValues.length === 0) {
        return prev.filter((item) => item.id !== attributeId);
      }

      return prev.map((item) =>
        item.id === attributeId ? { ...item, values: updatedValues } : item,
      );
    });
  };

  const resetProductList = () => {
    setDifferentProductLoader(true);
    if (selectedProduct?.restricted_collections.length > 0) {
      setCollectionLoader(false);
      setCollections(selectedProduct.restricted_collections);
      setSelectedCollection(0);
      setCollectionId(selectedProduct.restricted_collections[0].id);
      callDifferentProducts(selectedProduct.restricted_collections[0].id);
    } else {
      callDifferentProducts();
    }
  };

  const handleClearFilters = () => {
    clearFilterState();
    resetProductList();
  };

  const handleApplyFilters = () => {
    setOpenFilter(false);
    setDifferentProductLoader(true);

    if (attributeData.length === 0) {
      setActiveFilter(false);
      resetProductList();
      return;
    }

    getAttributeProducts(
      merchantConfig.merchant_id,
      rmsConfig.product_service_version,
      attributeData,
    )
      .then((resp) => {
        setFilteredVariantProducts(resp.data || []);
        setActiveFilter(true);
        setDifferentApiError(false);
      })
      .catch(() => {
        setDifferentApiError(true);
      })
      .finally(() => {
        setDifferentProductLoader(false);
      });
  };

  const callDifferentProducts = (collectionid, newOffset, viewMoreBool) => {
    if (
      isSelectionDrawerOpenRef.current ||
      activeListProductIdRef.current !== null ||
      variantsLoaderRef.current
    ) {
      return;
    }
    setFilteredVariantProducts([]);
    setActiveFilter(false);

    const reqOffset = Number(newOffset || 0);
    const reqCollection =
      collectionid == false ? undefined : (collectionid ?? undefined);
    const requestKey = JSON.stringify({
      collection: reqCollection || null,
      offset: reqOffset,
      query: query || null,
      selectedProductId: selectedProduct.product_id,
      selectedVariantId: selectedProduct.variant_id,
      qty: quantityInc,
    });

    // Strict de-dupe for same list query while a request is in flight
    if (
      productsRequestInFlightRef.current &&
      productsRequestKeyRef.current === requestKey
    ) {
      return;
    }

    productsRequestInFlightRef.current = true;
    productsRequestKeyRef.current = requestKey;
    if (reqOffset === 0) {
      setOffset(0);
      setShowViewMore(true);
    }
    const reqSeq = ++productsRequestSeqRef.current;

    getDifferentVariants(
      merchantConfig.merchant_id,
      selectedProduct.product_id,
      quantityInc,
      selectedProduct.product_price,
      reqOffset,
      reqCollection,
      query ? encodeURI(query) : null,
      null,
      selectedOrder.exchange_configurations.length > 0
        ? selectedOrder.exchange_configurations.includes("lower_value_product")
        : undefined,
      selectedOrder.exchange_configurations.length > 0
        ? selectedOrder.exchange_configurations.includes("higher_value_product")
        : undefined,
      selectedProduct.variant_id,
      selectedProduct.sku,
      rmsConfig.product_service_version,
    )
      .then((resp) => {
        if (reqSeq !== productsRequestSeqRef.current) {
          return;
        }
        if (resp.data.products.length === 0) {
          setShowViewMore(false);
        }
        if (!viewMoreBool) {
          for (let i = 0; i < resp.data.products.length; i++) {
            const sortedVariants = resp.data.products[i].variants.sort(
              (a, b) => a.position - b.position,
            );
            resp.data.products[i].variants = sortedVariants;
          }
          setDifferentProducts(resp.data.products);
        } else {
          for (let i = 0; i < resp.data.products.length; i++) {
            const sortedVariants = resp.data.products[i].variants.sort(
              (a, b) => a.position - b.position,
            );
            resp.data.products[i].variants = sortedVariants;
          }
          setDifferentProducts((prev) => prev.concat(resp.data.products));
        }
        setDifferentProductLoader(false);
        setViewMoreLoader(false);
        setDifferentApiError(false);
      })
      .catch((err) => {
        if (reqSeq !== productsRequestSeqRef.current) {
          return;
        }
        if (differentProducts.length > 0) {
          toast.error("Could not load more products. Try again.", {
            position: toast.POSITION.TOP_RIGHT,
            autoClose: 6000,
            hideProgressBar: false,
            closeOnClick: true,
          });
        } else {
          setDifferentApiError(true);
        }
      })
      .finally(() => {
        if (reqSeq === productsRequestSeqRef.current) {
          productsRequestInFlightRef.current = false;
        }
      });
  };

  useEffect(() => {
    if (!merchantConfig?.merchant_id) {
      return;
    }

    getAttributes(
      merchantConfig.merchant_id,
      rmsConfig.product_service_version,
    )
      .then((resp) => {
        setAllAttributes(resp.data || []);
      })
      .catch(() => {
        setAllAttributes([]);
      });
  }, [merchantConfig?.merchant_id, rmsConfig.product_service_version]);

  useEffect(() => {
    if (selectedProduct?.restricted_collections.length > 0) {
      setCollectionLoader(false);
      setCollections(selectedProduct.restricted_collections);
      setSelectedCollection(0);
      setCollectionId(selectedProduct.restricted_collections[0].id);
      callDifferentProducts(selectedProduct.restricted_collections[0].id);
    } else {
      callDifferentProducts();
    }
  }, []);

  useEffect(() => {
    if (searchInput == "") {
      setDifferentProductLoader(true);
      if (selectedProduct?.restricted_collections.length > 0) {
        setCollections(selectedProduct.restricted_collections);
        setSelectedCollection(0);
        setCollectionId(selectedProduct.restricted_collections[0].id);
        callDifferentProducts(selectedProduct.restricted_collections[0].id);
      } else {
        callDifferentProducts();
      }
    }
  }, [searchInput]);

  const handleSearch = (collectionid, newOffset) => {
    if (
      isSelectionDrawerOpenRef.current ||
      activeListProductIdRef.current !== null ||
      variantsLoaderRef.current
    ) {
      return;
    }
    setFilteredVariantProducts([]);
    setActiveFilter(false);

    const reqOffset = Number(newOffset || 0);
    const reqCollection = collectionid ?? undefined;
    const requestKey = JSON.stringify({
      collection: reqCollection || null,
      offset: reqOffset,
      query: query || null,
      selectedProductId: selectedProduct.product_id,
      selectedVariantId: selectedProduct.variant_id,
      qty: quantityInc,
    });

    if (
      productsRequestInFlightRef.current &&
      productsRequestKeyRef.current === requestKey
    ) {
      return;
    }

    productsRequestInFlightRef.current = true;
    productsRequestKeyRef.current = requestKey;
    if (reqOffset === 0) {
      setOffset(0);
      setShowViewMore(true);
    }
    const reqSeq = ++productsRequestSeqRef.current;

    getDifferentVariants(
      merchantConfig.merchant_id,
      selectedProduct.product_id,
      quantityInc,
      selectedProduct.product_price,
      reqOffset,
      reqCollection,
      query ? encodeURI(query) : null,
      null,
      selectedOrder.exchange_configurations.length > 0
        ? selectedOrder.exchange_configurations.includes("lower_value_product")
        : undefined,
      selectedOrder.exchange_configurations.length > 0
        ? selectedOrder.exchange_configurations.includes("higher_value_product")
        : undefined,
      selectedProduct.variant_id,
      selectedProduct.sku,
      rmsConfig.product_service_version,
    )
      .then((resp) => {
        if (reqSeq !== productsRequestSeqRef.current) {
          return;
        }
        setDifferentProducts(resp.data.products);
        setDifferentProductLoader(false);
        setViewMoreLoader(false);
        setDifferentApiError(false);
      })
      .catch(() => {
        if (reqSeq === productsRequestSeqRef.current) {
          setDifferentApiError(true);
        }
      })
      .finally(() => {
        if (reqSeq === productsRequestSeqRef.current) {
          productsRequestInFlightRef.current = false;
        }
      });
  };

  const handleCollectionSearch = (collection, i) => {
    setDifferentProductLoader(true);
    if (selectedCollection !== i) {
      callDifferentProducts(collection.collection_id);
      setSelectedCollection(i);
      setCollectionId(collection.collection_id);
    } else {
      if (selectedProduct?.restricted_collections.length > 0) {
        callDifferentProducts(selectedProduct.restricted_collections[0].id);
        setSelectedCollection(0);
        setCollections(selectedProduct.restricted_collections);
        setCollectionId(selectedProduct.restricted_collections[0].id);
      } else {
        callDifferentProducts();
        setSelectedCollection(null);
        setCollectionId(false);
      }
    }
  };

  const handleEnter = (e) => {
    if (e.key === "Enter") {
      setDifferentProductLoader(true);
      e.preventDefault();
      if (selectedProduct?.restricted_collections.length > 0) {
        handleSearch(selectedProduct?.restricted_collections[0].id);
      } else {
        handleSearch();
      }
    }
  };

  const handleProductExchange = () => {
    if (customSizeError) return;
    if (!effectiveSelectedVariant) {
      return;
    }
    if (setSelectedVariant) {
      setSelectedVariant(effectiveSelectedVariant);
    }
    dispatch(mobSelected(false));
    dispatch(updateWebSelected(false));
    dispatch(addNewProduct(requiredProduct));
    dispatch(returnQuantity(quantityInc));
    dispatch(returnType("exchange"));
    dispatch(addUploads([]));
    setUploads([]);
    let tempProducts = [...returnedProducts];
    let line_item_ids = tempProducts.map((a) => String(a.line_item_id));

    if (selectedGroupProducts.length > 0) {
      for (let i = selectedGroupProducts.length - 1; i >= 0; i--) {
        const product = selectedGroupProducts[i];

        const index = line_item_ids.indexOf(String(product.line_item_id));

        if (index !== -1) {
          tempProducts.splice(index, 1);
          line_item_ids.splice(index, 1);
        }
      }
    } else {
      if (line_item_ids.indexOf(String(selectedProduct.line_item_id)) !== -1) {
        let index = line_item_ids.indexOf(String(selectedProduct.line_item_id));
        tempProducts.splice(index, 1);
      }
    }

    let childProducts = selectedGroupProducts.filter(
      (groupProduct) =>
        groupProduct.is_child === true &&
        groupProduct.exchange_eligible === true &&
        groupProduct.auto_add === true,
    );
    const selectedLineMedia = uploadMediaIds.find(
      (item) =>
        String(item.line_item_id) === String(selectedProduct.line_item_id),
    );

    const nextReturnedProducts = [
      ...tempProducts,
      {
        merchant_id: merchantConfig.merchant_id,
        line_item_id: selectedProduct.line_item_id,

        product_id: selectedProduct.product_id,
        variant_id: selectedProduct.variant_id,
        product_price: selectedProduct.product_price,
        quantity: quantityInc,
        product_title: selectedProduct.product_title,
        variant_name: selectedProduct.variant_name,
        product_sku: selectedProduct.sku,
        original_product_price: selectedProduct.original_product_price,
        variant_weight: selectedProduct?.variant_weight || "",
        variant_weight_unit: selectedProduct?.variant_weight_unit || "",
        variant_image_url: selectedProduct.image_url,

        exchange_product_id: requiredProduct.store_product_id,
        exchange_variant_id: effectiveSelectedVariant.variant_id,
        exchange_product_sku: effectiveSelectedVariant.sku,
        exchange_product_price: effectiveSelectedVariant.price,
        exchange_product_title: requiredProduct.product_title,
        exchange_variant_image_url:
          (effectiveSelectedVariant.variant_img &&
            effectiveSelectedVariant.variant_img.src) ||
          effectiveSelectedVariant.variant_img ||
          effectiveSelectedVariant.variant_img.alt,
        exchange_variant_name: effectiveSelectedVariant.title,
        original_exchange_product_price: requiredProduct.original_product_price,
        exchange_variant_weight: effectiveSelectedVariant?.variant_weight || "",
        exchange_variant_weight_unit:
          effectiveSelectedVariant?.variant_weight_unit || "",

        return_type: "exchange",
        ...(reason?.reason_mode === "reason_first" ? {
          reason_mode: "reason_first",
          reason_resolution_config: getEffectiveResolutionConfig(reason, subReason, "exchange"),
        } : {}),
        refund_mode_offer_amount: 0, // Not sure why but we give based on line items as well

        customer_reason_code: reason.reason_code,
        questionnaire_answers: reason.questionnaire_answers || [],
        customer_reason_label: reason.reason_label,
        customer_reason_comment: otherReason,
        customer_sub_reason_code:
          subReason && subReason !== null ? subReason.reason_code : null,
        customer_sub_reason_label:
          subReason && subReason !== null ? subReason.reason_label : null,

        total_item_discount: selectedProduct.total_item_discount,
        discounted_price: selectedProduct.discounted_price,

        refund_applicable: selectedProduct.refund_applicable,
        exchange_applicable: selectedProduct.exchange_applicable,
        shipment_applicable: reason?.reason_mode === "reason_first"
          ? getEffectiveResolutionConfig(reason, subReason, "exchange")?.shipment_applicable ?? selectedProduct.shipment_applicable
          : selectedProduct.shipment_applicable,

        product_group_id:
          selectedProduct.product_group_id || customProductGroupId,
        is_parent: selectedProduct.is_parent,
        is_child: selectedProduct.is_child,
        properties: customProperties || selectedProduct.properties,

        // Not sure why, Just hard coded optional fields
        variant_length: 0,
        variant_breadth: 0,
        variant_height: 0,
        exchange_variant_length: 0,
        exchange_variant_breadth: 0,
        exchange_variant_height: 0,
        discount_applied: false,
        item_amount_difference: 0,
        total_item_amount_difference: 0,

        remaining_qty: selectedProduct.quantity - quantityInc,
        exchange_type: "different",
        applicable_refund_modes: null,
        uploaded_media: selectedLineMedia?.uploads || [],
        uploaded_media_ids: (selectedLineMedia?.uploads || []).map(
          (mediaItem) => mediaItem.media,
        ),
        media_previews: (selectedLineMedia?.uploads || []).map(
          (mediaItem) => mediaItem.url,
        ),
        ...getLineItemMetadata(selectedProduct),
      },
      ...childProducts.map((childProduct) => ({
        merchant_id: merchantConfig.merchant_id,
        line_item_id: childProduct.line_item_id,

        product_id: childProduct.product_id,
        variant_id: childProduct.variant_id,
        product_price: childProduct.product_price,
        quantity: quantityInc,
        product_title: childProduct.product_title,
        variant_name: childProduct.variant_name,
        product_sku: childProduct.sku,
        original_product_price: childProduct.original_product_price,
        variant_weight: childProduct?.variant_weight || "",
        variant_weight_unit: childProduct?.variant_weight_unit || "",
        variant_image_url: childProduct.image_url,

        exchange_product_id: childProduct.product_id,
        exchange_variant_id: childProduct.variant_id,
        exchange_product_sku: childProduct.sku,
        exchange_product_price: childProduct.product_price,
        exchange_product_title: childProduct.product_title,
        exchange_variant_image_url: childProduct.image_url,
        exchange_variant_name: childProduct.variant_name,
        original_exchange_product_price: childProduct.original_product_price,
        exchange_variant_weight: childProduct?.variant_weight || "",
        exchange_variant_weight_unit: childProduct?.variant_weight_unit || "",

        return_type: "exchange",
        ...(reason?.reason_mode === "reason_first" ? {
          reason_mode: "reason_first",
          reason_resolution_config: getEffectiveResolutionConfig(reason, subReason, "exchange"),
        } : {}),
        refund_mode_offer_amount: 0, // Not sure why but we give based on line items as well

        customer_reason_code: reason.reason_code,
        questionnaire_answers: reason.questionnaire_answers || [],
        customer_reason_label: reason.reason_label,
        customer_reason_comment: otherReason,
        customer_sub_reason_code:
          subReason && subReason !== null ? subReason.reason_code : null,
        customer_sub_reason_label:
          subReason && subReason !== null ? subReason.reason_label : null,

        total_item_discount: childProduct.total_item_discount,
        discounted_price: childProduct.discounted_price,

        exchange_applicable: childProduct.exchange_applicable,
        refund_applicable: childProduct.refund_applicable,
        shipment_applicable: reason?.reason_mode === "reason_first"
          ? getEffectiveResolutionConfig(reason, subReason, "exchange")?.shipment_applicable ?? childProduct.shipment_applicable
          : childProduct.shipment_applicable,

        product_group_id: childProduct.product_group_id,
        is_parent: childProduct.is_parent,
        is_child: childProduct.is_child,
        properties: childProduct.properties,

        // Not sure why, Just hard coded optional fields
        variant_length: 0,
        variant_breadth: 0,
        variant_height: 0,
        exchange_variant_length: 0,
        exchange_variant_breadth: 0,
        exchange_variant_height: 0,
        discount_applied: false,
        item_amount_difference: 0,
        total_item_amount_difference: 0,

        remaining_qty: childProduct.quantity - quantityInc,
        exchange_type: "different",
        applicable_refund_modes: null,
        ...getLineItemMetadata(childProduct),
      })),
    ];

    dispatch(addReturnedProducts(nextReturnedProducts));

    setAnimate(true);
    // Reset animation after it's done if needed
    setTimeout(() => {
      setAnimate(false);
    }, 700); // Duration matches the animation

    if (onExchangeComplete) {
      onExchangeComplete(nextReturnedProducts);
    }
  };

  const hasDiscount =
    rmsConfig.discount_config &&
    Object.keys(rmsConfig.discount_config).length > 0 &&
    rmsConfig.discount_config.target_type === "line_item";

  useEffect(() => {
    if (requiredProduct !== null) {
      let variantToSelect = null;

      if (selectedFilterVariantId) {
        variantToSelect = requiredProduct.variants.find(
          (v) => v.variant_id === selectedFilterVariantId,
        );
      }

      if (!variantToSelect) {
        variantToSelect = requiredProduct.variants.find(
          (a) => a.variant_id !== selectedProduct.variant_id,
        );
      }

      if (!variantToSelect && requiredProduct.variants.length > 0) {
        variantToSelect = requiredProduct.variants[0];
      }

      setLocalSelectedVariant(variantToSelect);
      setSelectedCategories(mapVariantAttributes(variantToSelect || {}));
      setSelectedFilterVariantId(null);
      selectedProductDetailRef.current =
        requiredProduct.product_id ||
        requiredProduct.id ||
        requiredProduct.store_product_id ||
        null;
    }
  }, [requiredProduct, selectedProduct.variant_id]);

  useEffect(() => {
    if (effectiveSelectedVariant && effectiveSelectedVariant !== null) {
      const variant = effectiveSelectedVariant || requiredProduct.variants[0];
      const originalPrice = Number(variant.price);

      let discountedPrice = originalPrice;

      if (hasDiscount) {
        if (rmsConfig.discount_config.type === "fixed") {
          discountedPrice =
            originalPrice - Number(rmsConfig.discount_config.value);
        } else {
          discountedPrice =
            originalPrice -
            (originalPrice * Number(rmsConfig.discount_config.value)) / 100;
        }
        discountedPrice = discountedPrice.toFixed(2);
        setDiscountPrice(discountedPrice);
      }
    }
  }, [effectiveSelectedVariant]);

  const viewMore = () => {
    if (
      isSelectionDrawerOpenRef.current ||
      activeListProductIdRef.current !== null ||
      variantsLoaderRef.current
    ) {
      return;
    }
    setViewMoreLoader(true);
    const nextOffset = offset + 12;
    callDifferentProducts(collectionId, nextOffset, true);
    setOffset(nextOffset);
  };

  useEffect(() => {
    activeListProductIdRef.current = activeListProductId;
  }, [activeListProductId]);

  useEffect(() => {
    isSelectionDrawerOpenRef.current = isSelectionDrawerOpen;
  }, [isSelectionDrawerOpen]);

  useEffect(() => {
    variantsLoaderRef.current = variantsLoader;
  }, [variantsLoader]);

  useEffect(() => {
    if (!infiniteScrollRef.current) {
      return undefined;
    }

    if (
      requiredProduct !== null ||
      differentApiError ||
      variantsLoader ||
      activeListProductId !== null ||
      activeFilter
    ) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (
          entry?.isIntersecting &&
          showViewMore &&
          !viewMoreLoader &&
          !differentProductLoader &&
          !variantsLoader &&
          activeListProductId === null
        ) {
          viewMore();
        }
      },
      { rootMargin: "240px 0px" },
    );

    observer.observe(infiniteScrollRef.current);
    return () => observer.disconnect();
  }, [
    requiredProduct,
    differentApiError,
    showViewMore,
    viewMoreLoader,
    differentProductLoader,
    variantsLoader,
    activeListProductId,
    activeFilter,
    offset,
    collectionId,
  ]);

  const orderRequiredProduct = (product) => {
    const unorderedProduct = buildDrawerProduct(product);

    // Sort the attributes
    unorderedProduct.attributes = unorderedProduct.attributes.sort((a, b) => {
      return a.position - b.position;
    });

    // Sort the 'values' array inside each attribute
    unorderedProduct.attributes.forEach((attribute) => {
      if (Array.isArray(attribute.values)) {
        attribute.values = attribute.values.sort((a, b) => a.order - b.order);
      }
    });

    const initialSelectedValues = {};

    unorderedProduct.attributes.forEach((category) => {
      initialSelectedValues[category.id] = category?.values?.[0]?.id;
    });

    const findSelectedVariant = (variants, attributes) => {
      return variants.find((variant) => {
        return attributes.every((attribute) => {
          const matchingVariantAttribute = variant.attributes.find(
            (variantAttribute) =>
              variantAttribute.product_attribute === attribute.id &&
              attribute.values.some(
                (value) => value.id === variantAttribute.value,
              ),
          );
          return Boolean(matchingVariantAttribute);
        });
      });
    };

    const defaultSelectedVariant = findSelectedVariant(
      unorderedProduct.variants,
      unorderedProduct.attributes,
    );

    updateRequiredProduct(unorderedProduct);
    const nextVariant =
      defaultSelectedVariant ||
      pickInitialVariant(unorderedProduct.variants) ||
      unorderedProduct.variants?.[0] ||
      null;
    setSelectedCategories(mapVariantAttributes(nextVariant || {}));
    setLocalSelectedVariant(nextVariant);
  };

  const handleSelectCategory = (categoryId, value) => {
    if (selectedCategories[categoryId] === value) {
      return;
    }

    // Update selected categories
    const updatedSelectedCategories = {
      ...selectedCategories,
      [categoryId]: value,
    };

    // Sort the selected categories by the category order (position) in `categories`
    const sortedSelectedCategories = Object.keys(updatedSelectedCategories)
      .sort((a, b) => {
        const positionA = requiredProduct.attributes.find(
          (cat) => cat.id === a,
        )?.position;
        const positionB = requiredProduct.attributes.find(
          (cat) => cat.id === b,
        )?.position;
        return positionA - positionB;
      })
      .reduce((acc, key) => {
        acc[key] = updatedSelectedCategories[key];
        return acc;
      }, {});

    // Set the sorted and normalized categories
    setSelectedCategories(sortedSelectedCategories);

    const findSelectedVariantWithMapping = (variants, selectedAttributes) => {
      return variants.find((variant) => {
        return Object.entries(selectedAttributes).every(
          ([productAttribute, value]) => {
            return variant.attributes.some(
              (variantAttribute) =>
                variantAttribute.product_attribute ===
                  parseInt(productAttribute, 10) &&
                variantAttribute.value === value,
            );
          },
        );
      });
    };

    const matchedVariant = findSelectedVariantWithMapping(
      requiredProduct.variants,
      sortedSelectedCategories,
    );

    if (
      matchedVariant &&
      (!effectiveSelectedVariant ||
        matchedVariant.variant_id !== effectiveSelectedVariant.variant_id)
    ) {
      setLocalSelectedVariant(matchedVariant);
      openSameVariantConfirmationIfNeeded(matchedVariant);
    }
  };

  const handleExchangeSelectionClick = () => {
    if (isSameVariantSelected) {
      setSameVariantConfirmationOpen(true);
      return;
    }

    handleProductExchange();
  };

  const renderSameVariantConfirmationModal = () => (
    <Modal
      aria-labelledby="same-variant-confirmation-title"
      aria-describedby="same-variant-confirmation-description"
      open={sameVariantConfirmationOpen}
      onClose={() => setSameVariantConfirmationOpen(false)}
      closeAfterTransition
    >
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          minWidth: "300px",
          padding: "20px 24px 24px",
          background: "var(--rms-v2-surface, #fff)",
          maxWidth: "460px",
          width: "90%",
          borderRadius: merchantConfig.use_rounded_edge_buttons
            ? "10px"
            : "0px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: "16px",
          }}
        >
          <div>
            <p
              id="same-variant-confirmation-title"
              style={{
                fontWeight: "600",
                fontSize: "16px",
                marginBottom: "8px",
              }}
            >
              Same size selected
            </p>
            <p
              id="same-variant-confirmation-description"
              style={{
                color: "var(--rms-v2-text-body, #3B444D)",
                fontSize: "14px",
              }}
            >
              You selected the same size {selectedProduct?.variant_name || ""}{" "}
              that you originally purchased. Do you want to continue with this
              exchange?
            </p>
          </div>
          <CloseIcon
            style={{
              width: "20px",
              height: "20px",
              stroke: "var(--rms-v2-text-body, #3B444D)",
              cursor: "pointer",
              flexShrink: 0,
            }}
            onClick={() => setSameVariantConfirmationOpen(false)}
          />
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
            marginTop: "24px",
          }}
        >
          <Button
            variant="outlined"
            color="primary"
            onClick={() => setSameVariantConfirmationOpen(false)}
          >
            No
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setSameVariantConfirmationOpen(false);
              handleProductExchange();
            }}
          >
            Yes, continue
          </Button>
        </div>
      </div>
    </Modal>
  );

  const getProductDetails = (product) => {
    const productId =
      product?.store_product_id || product?.product_id || product?.id || null;
    const requestKey = `${productId || "unknown"}`;

    if (
      variantRequestInFlightRef.current &&
      variantRequestKeyRef.current === requestKey
    ) {
      return;
    }

    variantRequestInFlightRef.current = true;
    variantRequestKeyRef.current = requestKey;
    getSameVariants(
      merchantConfig.merchant_id,
      productId,
      quantityInc,
      selectedProduct.product_price,
      selectedProduct.variant_id,
      selectedProduct.sku,
      selectedOrder.exchange_configurations.length > 0
        ? selectedOrder.exchange_configurations.includes("lower_value_product")
        : undefined,
      selectedOrder.exchange_configurations.length > 0
        ? selectedOrder.exchange_configurations.includes("higher_value_product")
        : undefined,
      rmsConfig.product_service_version,
    )
      .then((resp) => {
        if (!resp?.data?.products) {
          setVariantApiError(true);
          return;
        }
        orderRequiredProduct(resp.data.products);
        setVariantApiError(false);
      })
      .catch(() => {
        setVariantApiError(true);
      })
      .finally(() => {
        variantRequestInFlightRef.current = false;
        variantsLoaderRef.current = false;
        setVariantsLoader(false);
      });
  };

  const CategoryDisplay = ({ category }) => {
    const isVariantUnavailableForExchange = (variant) =>
      Boolean(
        variant &&
          (variant.out_of_stock ||
            !variant.quantity_available_for_exchange ||
            (merchantConfig.merchant_id === 541 &&
              variant.inventory_quantity < 7)),
      );

    const findSelectedVariantWithMapping = (variants, selectedAttributes) => {
      return variants.find((variant) => {
        return Object.entries(selectedAttributes).every(
          ([productAttribute, value]) => {
            return variant.attributes.some(
              (variantAttribute) =>
                variantAttribute.product_attribute ===
                  parseInt(productAttribute, 10) &&
                variantAttribute.value === value,
            );
          },
        );
      });
    };

    return (
      <div className="drawer-option-group">
        <p>{category.display_name}</p>
        <div className="drawer-option-list">
          {category.values.map((attr) => {
            const selected = selectedCategories[category.id] === attr.id;
            const candidateSelection = {
              ...selectedCategories,
              [category.id]: attr.id,
            };
            const candidateVariant = findSelectedVariantWithMapping(
              requiredProduct.variants,
              candidateSelection,
            );
            const isUnavailable = isVariantUnavailableForExchange(
              candidateVariant,
            );
            return (
              <div key={attr.id} style={{ position: "relative" }}>
                <input
                  type="radio"
                  id={`${category.id}-${attr.id}`}
                  name={category.name}
                  value={attr.value}
                  checked={selected}
                  onChange={() => handleSelectCategory(category.id, attr.id)}
                  style={{ display: "none" }}
                />
                <p
                  onClick={() => handleSelectCategory(category.id, attr.id)}
                  className={[
                    selected ? "selected" : "",
                    isUnavailable ? "variant-unavailable" : "",
                    selected && isUnavailable ? "variant-unavailable-selected" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  {attr.display_value}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const getProductKey = (product) =>
    product?.product_id || product?.id || product?.store_product_id;

  const getProductImage = (product) =>
    product?.images?.[0]?.src ||
    product?.variants?.[0]?.src ||
    product?.variants?.[0]?.variant_img?.src ||
    product?.variants?.[0]?.variant_img ||
    product?.variant_img?.src ||
    product?.variant_img ||
    "";

  const closeSelectionDrawer = () => {
    activeListProductIdRef.current = null;
    isSelectionDrawerOpenRef.current = false;
    variantsLoaderRef.current = false;
    updateRequiredProduct(null);
    setLocalSelectedVariant(null);
    setSelectedCategories({});
    setActiveListProductId(null);
    setIsSelectionDrawerOpen(false);
    setVariantsLoader(false);
    setVariantApiError(false);
  };

  const renderProductTile = (product, i, isFilteredProduct = false) => {
    const productKey = getProductKey(product) || product.variant_id || i;
    const isActive = activeListProductId === productKey;
    const imageUrl = getProductImage(product);
    const price =
      product?.price ||
      product?.variants?.[0]?.price ||
      product?.product_price ||
      "";

    return (
      <Grid
        xs={6}
        md={6}
        sx={{
          display: "flex",
          cursor: "pointer",
          flexDirection: "column",
          alignItems: "stretch",
          marginBottom: "1rem",
        }}
        item
        onClick={() => {
          const drawerProduct = buildDrawerProduct(product);
          const initialVariant =
            pickInitialVariant(drawerProduct.variants) ||
            drawerProduct.variants?.[0] ||
            null;

          activeListProductIdRef.current = productKey;
          isSelectionDrawerOpenRef.current = true;
          variantsLoaderRef.current = true;
          setActiveListProductId(productKey);
          setSelectedFilterVariantId(
            isFilteredProduct ? product.variant_id : null,
          );
          setVariantApiError(false);
          updateRequiredProduct(null);
          setLocalSelectedVariant(null);
          setSelectedCategories(mapVariantAttributes(initialVariant || {}));
          drawerOpenedAtRef.current = Date.now();
          setIsSelectionDrawerOpen(true);
          setVariantsLoader(true);
          getProductDetails(product);
        }}
        key={productKey}
      >
        <div
          style={{
            width: "100%",
            minHeight: "100%",
            background: isActive
              ? `${merchantConfig.primary_theme_color}0F`
              : "var(--rms-v2-surface)",
            border: isActive
              ? `2px solid ${merchantConfig.primary_theme_color}`
              : "1px solid var(--rms-v2-border)",
            borderRadius: merchantConfig.use_rounded_edge_buttons
              ? "10px"
              : "0px",
            overflow: "hidden",
            position: "relative",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {isActive && (
            <div
              style={{
                position: "absolute",
                top: "10px",
                right: "10px",
                width: "22px",
                height: "22px",
                borderRadius: "50%",
                background: merchantConfig.primary_theme_color,
                color: "var(--rms-v2-surface)",
                fontSize: "14px",
                fontWeight: 800,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 2,
                boxShadow: "0 0 0 3px var(--rms-v2-surface)",
              }}
            >
              ✓
            </div>
          )}
          <div
            style={{
              width: "100%",
              aspectRatio: "1 / 1",
              height: "calc((min(100vw, 500px) - 72px) / 2)",
              background: "var(--rms-v2-surface-muted)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={product.product_title}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  display: "block",
                }}
              />
            ) : (
              <span
                style={{
                  color: "#98A2B3",
                  fontSize: "12px",
                  fontWeight: 700,
                }}
              >
                No image
              </span>
            )}
          </div>
          <div
            style={{
              padding: "12px 14px",
              background: isActive
                ? `${merchantConfig.primary_theme_color}0F`
                : "var(--rms-v2-surface)",
              display: "flex",
              flexDirection: "column",
              gap: "6px",
            }}
          >
            {product?.secondary_display_title && (
              <p
                style={{
                  margin: "0 0 8px",
                  color: merchantConfig.primary_theme_color,
                  fontSize: "10px",
                  fontWeight: 800,
                  letterSpacing: "0.5px",
                  textTransform: "uppercase",
                }}
              >
                {product.secondary_display_title}
              </p>
            )}
            <p
              style={{
                margin: 0,
                color: "var(--rms-v2-text-title)",
                fontSize: "14px",
                fontWeight: 800,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {product.product_title}
            </p>
            {price !== "" && (
              <p
                style={{
                  color: "var(--rms-v2-text-title)",
                  fontSize: "16px",
                  margin: 0,
                  fontWeight: "800",
                }}
              >
                ₹ {price}
              </p>
            )}
            {product.out_of_stock && (
              <div className="exchange-feedback exchange-feedback--error out-of-stock out-of-stock-tile">
                <InfoOutlinedIcon className="exchange-feedback-icon" />
                <div className="exchange-feedback-content">
                  <p className="exchange-feedback-title">
                    Product not available for exchange
                  </p>
                  <p className="exchange-feedback-text">Out of stock</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </Grid>
    );
  };

  const DRAWER_CLOSE_GUARD_MS = 650;

  const renderSelectionDrawer = (content) => (
    <>
      <div
        onClick={(event) => {
          event.stopPropagation();
          // Mobile browsers can dispatch delayed synthetic click after tile tap.
          // Guard window keeps first delayed backdrop click from instantly closing drawer.
          if (Date.now() - drawerOpenedAtRef.current < DRAWER_CLOSE_GUARD_MS) {
            return;
          }
          closeSelectionDrawer();
        }}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(17, 24, 39, 0.46)",
          zIndex: 1200,
        }}
      />
	      <div
	        style={{
	          position: "fixed",
	          left: "50%",
	          bottom: 0,
	          transform: "translateX(-50%)",
	          width: "100%",
	          maxWidth: "500px",
	          maxHeight: "88vh",
	          display: "flex",
	          flexDirection: "column",
	          overflow: "visible",
	          margin: 0,
	          padding: "0",
	          background: "var(--rms-v2-surface)",
          borderRadius: merchantConfig.use_rounded_edge_buttons
            ? "10px 10px 0 0"
            : "0px",
          zIndex: 1201,
          boxSizing: "border-box",
          animation: "v2SlideUp 220ms ease-out",
	        }}
	      >
	        <style>{`@keyframes v2SlideUp{from{transform:translateX(-50%) translateY(22px);opacity:.7}to{transform:translateX(-50%) translateY(0);opacity:1}}`}</style>
	        <FloatingCloseButton
	          ariaLabel="Close replacement drawer"
	          className="drawer-close"
	          iconSize={14}
	          onClick={closeSelectionDrawer}
	        />
	        {content}
	      </div>
    </>
  );

  const renderVariantSelectionContent = () => {
    if (!requiredProduct) {
      return null;
    }

    const currentVariant =
      effectiveSelectedVariant || requiredProduct.variants?.[0];
    const imageUrl =
      currentVariant?.variant_img?.src ||
      currentVariant?.variant_img ||
      currentVariant?.src ||
      requiredProduct?.images?.[0]?.src ||
      "";
    const currentPrice = Number(
      currentVariant?.price || requiredProduct.variants?.[0]?.price || 0,
    );
    const originalPrice = Number(selectedProduct?.product_price || 0);
    const priceDifference = currentPrice - originalPrice;
    const priceDifferenceLabel =
      priceDifference === 0
        ? "No price difference"
        : `${priceDifference > 0 ? "+" : "-"}₹${Math.abs(priceDifference).toLocaleString("en-IN")} difference`;
    const isUnavailable =
      currentVariant &&
      !isSameVariantSelected &&
      (currentVariant.out_of_stock ||
        !currentVariant.quantity_available_for_exchange ||
        (merchantConfig.merchant_id === 541 &&
          currentVariant.inventory_quantity < 7));

    return (
      <>
        <StyledDifferentProduct
          rounded={merchantConfig.use_rounded_edge_buttons}
          primaryColor={merchantConfig.primary_theme_color}
          className="replacement-drawer"
        >
	          <div className="drawer-handle" />
	          <div className="drawer-header">
	            <h2>Customize replacement</h2>
	          </div>
          <div className="drawer-product">
            <div className="drawer-product-image">
              {imageUrl ? (
                <img src={imageUrl} alt="Replacement product" />
              ) : (
                <span>No image</span>
              )}
            </div>
            <div className="drawer-product-info">
              <p className="drawer-product-title">
                {requiredProduct.product_title || requiredProduct.title}
              </p>
              <p className="drawer-product-price">
                ₹{currentPrice.toLocaleString("en-IN")}
                <span>{priceDifferenceLabel}</span>
              </p>
            </div>
          </div>
          <div className="product-details">
            {merchantConfig.merchant_id === 977 && (
              <div>
                {requiredProduct.attributes
                  ?.filter((category) => category.name !== "size")
                  .map((category) => (
                    <CategoryDisplay key={category.id} category={category} />
                  ))}
                {requiredProduct.attributes
                  ?.filter((category) => category.name === "size")
                  .map((category) => {
                    const grouped = category.values.reduce((acc, item) => {
                      const types = ["single", "double", "queen", "king"];
                      const type = types.find((t) => item.value.includes(t));
                      if (!type) return acc;
                      acc[type] = acc[type] || [];
                      acc[type].push({
                        ...item,
                        group: type,
                        display_value: item.display_value
                          .replace(/\s*\(\w+\)\s*$/, "")
                          .trim(),
                      });
                      return acc;
                    }, {});

                    return Object.keys(grouped).length > 0 ? (
                      <SizeSelector
                        key={category.id}
                        grouped={grouped}
                        sizeAttributeId={category.id}
                        onCustomSizeError={setCustomSizeError}
                        variants={requiredProduct.variants}
                        setSelectedVariant={setLocalSelectedVariant}
                        onVariantSelected={
                          openSameVariantConfirmationIfNeeded
                        }
                        selectedVariant={effectiveSelectedVariant}
                        setCustomProperties={setCustomProperties}
                        selectedProduct={selectedProduct}
                        setCustomProductGroupId={setCustomProductGroupId}
                      />
                    ) : (
                      <CategoryDisplay key={category.id} category={category} />
                    );
                  })}
              </div>
            )}
            {merchantConfig.merchant_id !== 977 &&
              requiredProduct.attributes?.map((category) => (
                <CategoryDisplay key={category.id} category={category} />
              ))}
            {isUnavailable && (
              <div className="exchange-feedback exchange-feedback--error out-of-stock">
                <InfoOutlinedIcon className="exchange-feedback-icon" />
                <div className="exchange-feedback-content">
                  {currentVariant?.errors?.length > 0 ? (
                    <ul className="exchange-feedback-list">
                      {currentVariant.errors.map((error) => (
                        <li key={error.error_code}>{error.error_code_message}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="exchange-feedback-text">Out of stock</p>
                  )}
                </div>
              </div>
            )}
            {currentVariant &&
              selectedProduct &&
              isSameVariantSelected && (
                <div className="exchange-feedback exchange-feedback--info same-variant-alert">
                  <InfoOutlinedIcon className="exchange-feedback-icon" />
                  <div className="exchange-feedback-content">
                    <p className="exchange-feedback-text">
                      You have selected the same variant{" "}
                      {selectedProduct.variant_name} that you originally
                      purchased.
                    </p>
                  </div>
                </div>
              )}
          </div>
        </StyledDifferentProduct>

        {/* <div
          style={{ padding: "0px 1rem", fontSize: "14px", marginTop: "10px" }}
        >
          {parse(requiredProduct.rich_description || "")}
        </div> */}
	        <div
	          className="drawer-action-bar"
	          style={{
	            background: "var(--rms-v2-surface)",
	            borderTop: "1px solid var(--rms-v2-border-soft)",
	            flex: "0 0 auto",
	            padding: "14px 18px 18px",
	            position: "relative",
	            zIndex: 2,
	          }}
	        >
          <div style={{ position: "relative", width: "100%" }}>
            <Button
              color="primary"
              fullWidth
              onClick={handleExchangeSelectionClick}
              variant="contained"
              disabled={customSizeError || isUnavailable}
            >
              {actionLabel}
            </Button>
            {isUnavailable && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background: "rgba(255,255,255,0.68)",
                  borderRadius: merchantConfig.use_rounded_edge_buttons
                    ? "10px 10px 0 0"
                    : "0px",
                  pointerEvents: "none",
                }}
              />
            )}
          </div>
        </div>
        {renderSameVariantConfirmationModal()}
      </>
    );
  };

  const renderFilterPanel = () => (
    <Modal open={openFilter} onClose={() => setOpenFilter(false)}>
      <div
        style={{
          position: "fixed",
          left: "50%",
          bottom: 0,
          transform: "translateX(-50%)",
          width: "100%",
          maxWidth: "500px",
          maxHeight: "82vh",
          background: "var(--rms-v2-surface)",
          borderTopLeftRadius: merchantConfig.use_rounded_edge_buttons
            ? "14px"
            : "0px",
          borderTopRightRadius: merchantConfig.use_rounded_edge_buttons
            ? "14px"
            : "0px",
          boxShadow: "0 -16px 42px rgba(29, 42, 64, 0.18)",
          overflow: "hidden",
          outline: "none",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 18px",
            borderBottom: "1px solid var(--rms-v2-border-soft)",
          }}
        >
          <p
            style={{
              margin: 0,
              color: "var(--rms-v2-text-title)",
              fontSize: "16px",
              fontWeight: 900,
            }}
          >
            Filters
          </p>
          <Button
            variant="text"
            color="primary"
            onClick={handleClearFilters}
            style={{
              fontSize: "12px",
              fontWeight: 800,
              borderRadius: merchantConfig.use_rounded_edge_buttons
                ? "8px"
                : "0px",
            }}
          >
            Clear All
          </Button>
        </div>

        <div style={{ display: "flex", minHeight: "320px", maxHeight: "58vh" }}>
          <div
            style={{
              width: "42%",
              overflowY: "auto",
              borderRight: "1px solid var(--rms-v2-border-soft)",
              background: "var(--rms-v2-section-bg)",
            }}
          >
            {allAttributes.length > 0 ? (
              allAttributes.map((attribute) => (
                <button
                  type="button"
                  key={attribute.id}
                  onClick={() => {
                    setOpenAttributeValues(attribute.values || []);
                    setOpenAttributeId(attribute.id);
                  }}
                  style={{
                    width: "100%",
                    minHeight: "48px",
                    padding: "10px 14px",
                    border: 0,
                    borderBottom: "1px solid var(--rms-v2-border-soft)",
                    background:
                      openAttributeId === attribute.id
                        ? "var(--rms-v2-surface)"
                        : "transparent",
                    color: "var(--rms-v2-text-title)",
                    fontSize: "12px",
                    fontWeight: openAttributeId === attribute.id ? 900 : 700,
                    textAlign: "left",
                    cursor: "pointer",
                  }}
                >
                  {attribute.display_name || attribute.name}
                </button>
              ))
            ) : (
              <p
                style={{
                  margin: "16px",
                  color: "var(--rms-v2-text-muted)",
                  fontSize: "13px",
                }}
              >
                No filters found
              </p>
            )}
          </div>

          <div style={{ flex: 1, overflowY: "auto", padding: "8px 0" }}>
            {openAttributeValues.length > 0 ? (
              openAttributeValues.map((attributeValue) => {
                const selected = isValueSelected(attributeValue.id);

                return (
                  <button
                    type="button"
                    key={attributeValue.id}
                    onClick={() =>
                      toggleAttributeValue(openAttributeId, attributeValue.id)
                    }
                    style={{
                      width: "100%",
                      minHeight: "46px",
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      padding: "8px 16px",
                      border: 0,
                      background: "transparent",
                      color: selected
                        ? merchantConfig.primary_theme_color
                        : "var(--rms-v2-text-body)",
                      fontSize: "13px",
                      fontWeight: selected ? 900 : 600,
                      textAlign: "left",
                      cursor: "pointer",
                    }}
                  >
                    <span
                      style={{
                        width: "18px",
                        height: "18px",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: merchantConfig.use_rounded_edge_buttons
                          ? "5px"
                          : "0px",
                        border: `1px solid ${
                          selected
                            ? merchantConfig.primary_theme_color
                            : "var(--rms-v2-border)"
                        }`,
                        color: selected
                          ? merchantConfig.primary_theme_color
                          : "transparent",
                        flex: "0 0 auto",
                      }}
                    >
                      ✓
                    </span>
                    {attributeValue.display_value || attributeValue.value}
                  </button>
                );
              })
            ) : (
              <p
                style={{
                  margin: "18px",
                  color: "var(--rms-v2-text-muted)",
                  fontSize: "13px",
                }}
              >
                Select a filter
              </p>
            )}
          </div>
        </div>

        <div
          style={{
            display: "flex",
            gap: "10px",
            padding: "14px 18px",
            borderTop: "1px solid var(--rms-v2-border-soft)",
          }}
        >
          <Button
            variant="outlined"
            color="primary"
            fullWidth
            onClick={() => setOpenFilter(false)}
          >
            Close
          </Button>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={handleApplyFilters}
          >
            Apply
          </Button>
        </div>
      </div>
    </Modal>
  );

  const renderProductList = () => (
    <div>
      {renderFilterPanel()}
      <div
        ref={replacementSearchRef}
        className={`replacement-search${
          isReplacementSearchStuck ? " is-stuck" : ""
        }`}
        style={{
          maxWidth: "inherit",
          margin: 0,
          padding: "1rem 0 0.75rem",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            borderRadius: merchantConfig.use_rounded_edge_buttons
              ? "10px"
              : "0px",
            border: "1px solid var(--rms-v2-border-soft)",
            minHeight: "52px",
            background: "var(--rms-v2-surface)",
          }}
        >
          <SearchIcon
            style={{
              color: "#8A94A6",
              marginLeft: "14px",
              width: "22px",
              height: "22px",
              flexShrink: 0,
            }}
          />
          <div
            style={{
              display: "flex",
              alignItems: "center",
              padding: "4px 0rem",
              flex: 1,
            }}
          >
            <input
              type="text"
              placeholder="Search replacement product..."
              value={searchInput || ""}
              onChange={(e) => {
                setSearchInput(e.target.value);
                if (activeFilter) {
                  clearFilterState();
                }
                setQuery(e.target.value);
              }}
              onKeyDown={(e) => handleEnter(e)}
              style={{
                border: "none",
                background: "transparent",
                width: "100%",
                padding: "0px",
                marginLeft: "10px",
                outline: "none",
                color: "var(--rms-v2-text-title)",
                fontSize: "15px",
                fontFamily:
                  merchantConfig && merchantConfig.google_font_family !== null
                    ? merchantConfig.google_font_family
                    : "Inter",
              }}
            />
            {searchInput !== null &&
              searchInput.length > 0 &&
              searchInput !== "" && (
                <p
                  style={{ cursor: "pointer", margin: "6px 8px 0px 0px" }}
                  onClick={() => {
                    setSearchInput("");
                    setQuery("");
                    if (activeFilter) {
                      clearFilterState();
                    }
                  }}
                >
                  <CloseIcon
                    style={{
                      width: "18px",
                      height: "18px",
                      stroke: "var(--rms-v2-text-body)",
                    }}
                  />
                </p>
              )}
          </div>
          <button
            type="button"
            aria-label="Filters"
            onClick={() => {
              if (allAttributes.length > 0 && !openAttributeId) {
                setOpenAttributeId(allAttributes[0].id);
                setOpenAttributeValues(allAttributes[0].values || []);
              }
              setOpenFilter(true);
            }}
            style={{
              width: "44px",
              alignSelf: "stretch",
              border: 0,
              borderLeft: "1px solid var(--rms-v2-border-soft)",
              background: activeFilter
                ? `${merchantConfig.primary_theme_color}1A`
                : "transparent",
              color: merchantConfig.primary_theme_color,
              cursor: "pointer",
              position: "relative",
            }}
          >
            <FilterAltOutlinedIcon style={{ fontSize: "20px" }} />
            {activeFilter && (
              <span
                style={{
                  position: "absolute",
                  top: "10px",
                  right: "10px",
                  width: "7px",
                  height: "7px",
                  borderRadius: "50%",
                  background: merchantConfig.primary_theme_color,
                }}
              />
            )}
          </button>
        </div>
      </div>

      {collectionLoader ? (
        <p
          style={{
            margin: "20px 16px 0px",
            color: "var(--rms-v2-text-muted)",
            fontSize: "13px",
          }}
        >
          Loading collections...
        </p>
      ) : collections.length > 0 ? (
        <div className="collections-section">
          {collections.map((collection, i) => (
            <p
              onClick={() => handleCollectionSearch(collection, i)}
              key={collection.id || collection.collection_id}
              style={{
                padding: "7px 16px",
                background:
                  selectedCollection === i
                    ? merchantConfig.primary_theme_color
                    : "var(--rms-v2-surface)",
                color:
                  selectedCollection === i
                    ? "var(--rms-v2-surface)"
                    : "#4B5565",
                width: "fit-content",
                whiteSpace: "nowrap",
                borderRadius: merchantConfig.use_rounded_edge_buttons
                  ? "10px"
                  : "0px",
                marginRight: "10px",
                fontSize: "11px",
                fontWeight: 800,
                cursor: "pointer",
                border: "1px solid var(--rms-v2-border-soft)",
              }}
            >
              {collection.title}
            </p>
          ))}
        </div>
      ) : null}

      <div>
        {differentProductLoader ? (
          <p
            style={{
              padding: "10px 24px",
              color: "var(--rms-v2-text-muted)",
              fontSize: "14px",
            }}
          >
            Loading products...
          </p>
        ) : differentApiError ? (
          <LoadingError marginTop="100px" />
        ) : activeFilter ? (
          filteredVariantProducts.length > 0 ? (
            <Grid
              container
              columnSpacing={2}
              rowSpacing={{ xs: 0, md: 2 }}
              columns={12}
              style={{ padding: "0px", marginTop: 0 }}
            >
              {filteredVariantProducts.map((product, i) =>
                renderProductTile(product, i, true),
              )}
            </Grid>
          ) : (
            <Grid
              container
              columns={12}
              style={{
                padding: "0px 24px",
                justifyContent: "center",
                marginTop: 0,
              }}
            >
              <Grid
                item
                style={{
                  justifyContent: "center",
                  textAlign: "center",
                  marginTop: "4rem",
                }}
              >
                <Search />
                <p style={{ textAlign: "center", marginTop: "1rem" }}>
                  No products match selected filters
                </p>
              </Grid>
            </Grid>
          )
        ) : differentProducts.length > 0 ? (
          <Grid
            container
            columnSpacing={2}
            rowSpacing={{ xs: 0, md: 2 }}
            columns={12}
            style={{ padding: "0px", marginTop: 0 }}
          >
            {differentProducts.map((product, i) =>
              renderProductTile(product, i),
            )}
          </Grid>
        ) : (
          <Grid
            container
            columns={12}
            style={{
              padding: "0px 24px",
              justifyContent: "center",
              marginTop: 0,
            }}
          >
            <Grid
              item
              style={{
                justifyContent: "center",
                textAlign: "center",
                marginTop: "4rem",
              }}
            >
              <Search />
              <p style={{ textAlign: "center", marginTop: "1rem" }}>
                {(query !== null && query.length > 0 && query !== "") ||
                collectionId !== null
                  ? "Hmm we don't have that..yet"
                  : "Start typing to search new products"}
              </p>
            </Grid>
          </Grid>
        )}
        {!activeFilter && viewMoreLoader && (
          <p
            style={{
              padding: "8px 24px",
              color: "var(--rms-v2-text-muted)",
              fontSize: "13px",
            }}
          >
            Loading more...
          </p>
        )}
        <div ref={infiniteScrollRef} style={{ height: "1px" }} />
      </div>
    </div>
  );

  return (
    <div>
      {variantsLoader && !pageMode ? (
        <p
          style={{
            padding: "12px 0",
            color: "var(--rms-v2-text-muted)",
            fontSize: "14px",
          }}
        >
          Loading product...
        </p>
      ) : pageMode ? (
        <>
          {renderProductList()}
          {isSelectionDrawerOpen &&
            variantsLoader &&
            activeListProductId !== null &&
            renderSelectionDrawer(
              <div style={{ padding: "10px 18px 24px" }}>
                <p
                  style={{
                    margin: 0,
                    color: "var(--rms-v2-text-title)",
                    fontSize: "16px",
                    fontWeight: 800,
                  }}
                >
                  Loading variants...
                </p>
                <div
                  style={{
                    height: "120px",
                    marginTop: "14px",
                    borderRadius: merchantConfig.use_rounded_edge_buttons
                      ? "10px"
                      : "0px",
                    background: "var(--rms-v2-surface-muted)",
                  }}
                />
              </div>,
            )}
          {isSelectionDrawerOpen &&
            variantApiError &&
            !variantsLoader &&
            requiredProduct === null &&
            renderSelectionDrawer(
              <div style={{ padding: "16px 18px 24px" }}>
                <p
                  style={{
                    margin: 0,
                    color: "var(--rms-v2-text-title)",
                    fontSize: "16px",
                    fontWeight: 800,
                  }}
                >
                  Couldn&apos;t load variants
                </p>
                <p
                  style={{
                    margin: "10px 0 0",
                    color: "var(--rms-v2-text-muted)",
                    fontSize: "13px",
                  }}
                >
                  Please select this product again.
                </p>
                <Button
                  variant="contained"
                  fullWidth
                  style={{
                    marginTop: "14px",
                    height: "40px",
                    minHeight: "40px",
                    borderRadius: merchantConfig.use_rounded_edge_buttons
                      ? "10px"
                      : "0px",
                  }}
                  onClick={() => {
                    activeListProductIdRef.current = null;
                    isSelectionDrawerOpenRef.current = false;
                    variantsLoaderRef.current = false;
                    updateRequiredProduct(null);
                    setLocalSelectedVariant(null);
                    setSelectedCategories({});
                    setVariantApiError(false);
                    setIsSelectionDrawerOpen(false);
                    setActiveListProductId(null);
                  }}
                >
                  Close
                </Button>
              </div>,
            )}
          {isSelectionDrawerOpen &&
            requiredProduct !== null &&
            !variantsLoader &&
            renderSelectionDrawer(renderVariantSelectionContent())}
        </>
      ) : requiredProduct === null ? (
        <div>
          <div
            ref={replacementSearchRef}
            className={`replacement-search${
              isReplacementSearchStuck ? " is-stuck" : ""
            }`}
            style={{
              maxWidth: "inherit",
              margin: 0,
              padding: "1rem 0 0.75rem",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                borderRadius: merchantConfig.use_rounded_edge_buttons
                  ? "10px"
                  : "0px",
                border: "1px solid var(--rms-v2-border-soft)",
                minHeight: "52px",
                background: "var(--rms-v2-surface)",
              }}
            >
              <SearchIcon
                style={{
                  color: "#8A94A6",
                  marginLeft: "14px",
                  width: "22px",
                  height: "22px",
                  flexShrink: 0,
                }}
              />
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "4px 0rem",
                  flex: 1,
                }}
              >
                <input
                  type="text"
                  placeholder="Search replacement product..."
                  value={searchInput || ""}
                  onChange={(e) => {
                    setSearchInput(e.target.value);
                    setQuery(e.target.value);
                  }}
                  onKeyDown={(e) => handleEnter(e)}
                  style={{
                    border: "none",
                    background: "transparent",
                    width: "100%",
                    padding: "0px",
                    marginLeft: "10px",
                    outline: "none",
                    color: "var(--rms-v2-text-title)",
                    fontSize: "15px",
                    fontFamily:
                      merchantConfig &&
                      merchantConfig.google_font_family !== null
                        ? merchantConfig.google_font_family
                        : "Inter",
                  }}
                />
                {searchInput !== null &&
                  searchInput.length > 0 &&
                  searchInput !== "" && (
                    <p
                      style={{ cursor: "pointer", margin: "6px 8px 0px 0px" }}
                      onClick={() => {
                        setSearchInput("");
                        setQuery("");
                      }}
                    >
                      <CloseIcon
                        style={{
                          width: "18px",
                          height: "18px",
                          stroke: "var(--rms-v2-text-body)",
                        }}
                      />
                    </p>
                  )}
              </div>
            </div>
          </div>

          {collectionLoader ? (
            <p
              style={{
                margin: "20px 16px 0px",
                color: "var(--rms-v2-text-muted)",
                fontSize: "13px",
              }}
            >
              Loading collections...
            </p>
          ) : collections.length > 0 ? (
            <div className="collections-section">
              {collections.map((collection, i) => (
                <p
                  onClick={() => handleCollectionSearch(collection, i)}
                  key={collection.id || collection.collection_id}
                  style={{
                    padding: "7px 16px",
                    background:
                      selectedCollection === i
                        ? merchantConfig.primary_theme_color
                        : "var(--rms-v2-surface)",
                    color:
                      selectedCollection === i
                        ? "var(--rms-v2-surface)"
                        : "#4B5565",
                    width: "fit-content",
                    whiteSpace: "nowrap",
                    borderRadius: merchantConfig.use_rounded_edge_buttons
                      ? "10px"
                      : "0px",
                    marginRight: "10px",
                    fontSize: "11px",
                    fontWeight: 800,
                    cursor: "pointer",
                    border: "1px solid var(--rms-v2-border-soft)",
                  }}
                >
                  {collection.title}
                </p>
              ))}
            </div>
          ) : null}

          <div>
            {differentProductLoader ? (
              <p
                style={{
                  padding: "10px 24px",
                  color: "var(--rms-v2-text-muted)",
                  fontSize: "14px",
                }}
              >
                Loading products...
              </p>
            ) : differentApiError ? (
              <LoadingError marginTop="100px" />
            ) : differentProducts.length > 0 ? (
              <Grid
                container
                columnSpacing={2}
                rowSpacing={{ xs: 0, md: 2 }}
                columns={12}
                style={{ padding: "0px", marginTop: 0 }}
              >
                {differentProducts.map((product, i) =>
                  renderProductTile(product, i),
                )}
              </Grid>
            ) : (
              <Grid
                container
                columns={12}
                style={{
                  padding: "0px 24px",
                  justifyContent: "center",
                  marginTop: 0,
                }}
              >
                <Grid
                  item
                  style={{
                    justifyContent: "center",
                    textAlign: "center",
                    marginTop: "4rem",
                  }}
                >
                  <Search />
                  <p style={{ textAlign: "center", marginTop: "1rem" }}>
                    {(query !== null && query.length > 0 && query !== "") ||
                    collectionId !== null
                      ? "Hmm we don't have that..yet"
                      : "Start typing to search new products"}
                  </p>
                </Grid>
              </Grid>
            )}
            {viewMoreLoader && (
              <p
                style={{
                  padding: "8px 24px",
                  color: "var(--rms-v2-text-muted)",
                  fontSize: "13px",
                }}
              >
                Loading more...
              </p>
            )}
            <div ref={infiniteScrollRef} style={{ height: "1px" }} />
          </div>
        </div>
      ) : (
        <div style={{ margin: "10px 0px" }}>
          {renderVariantSelectionContent()}
        </div>
      )}
    </div>
  );
}

export default DifferentProduct;
